import { CheckCircle2, CircleHelp, Download, Eye, EyeOff, FileSignature, GitCompare, Handshake, MessageSquare, Pencil, Send, XCircle } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { confirmModal, Modal, promptModal } from '@/components/ui/modal'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Textarea } from '@/components/ui/textarea'
import { CheckRow } from '@/components/common/FormBits'
import { useProject } from '@/features/projects/api'
import { cn } from '@/lib/utils'
import { fmtDateTime, fmtMoney } from '../../../shared/format'
import { useSetOfferStatus } from '../../api'
import { downloadQuote, FLOW_TONE, useQuoteAction, useQuoteFlow } from '../../quotes'
import type { Offer } from '../../types'
import { toastError, useCrm } from '../../ui'
import { OfferForm } from '../forms/OfferForm'
import { revisionDiff } from './revisionDiff'
import { SendQuoteModal } from './SendQuoteModal'

type Tab = 'process' | 'revisions' | 'sends' | 'visibility'

/** Teklif süreci: durum ve onay kademesi, kararlar, gönderim, pazarlık, revizyon farkları, yorumlar ve geçmişler. */
export function QuoteFlowModal({ offer, onClose }: { offer: Offer; onClose: () => void }) {
  const { t } = useTranslation()
  const { staffName } = useCrm()
  const q = useQuoteFlow(offer.id)
  const act = useQuoteAction()
  const setStatus = useSetOfferStatus()
  const project = useProject(offer.projectId || null)
  const [tab, setTab] = useState<Tab>('process')
  const [note, setNote] = useState('')
  const [final, setFinal] = useState(offer.isFinal)
  const [editing, setEditing] = useState(false)
  const [sending, setSending] = useState(false)
  const f = q.data
  const names = (code: string) => project.data?.modules.find((m) => m.code === code)?.name ?? code
  const run = (action: 'submit' | 'decide' | 'negotiate' | 'comments', body: Record<string, unknown>, done: string) =>
    act.mutate({ id: offer.id, action, body }, { onSuccess: () => (toast.success(t(done)), setNote('')), onError: toastError })
  const needNote = !note.trim()

  const togglePrices = async () => {
    if (!f) return
    const reason = await promptModal({
      title: t(f.showLinePrices ? 'crm.quote.hidePricesTitle' : 'crm.quote.showPricesTitle'),
      description: t(f.showLinePrices ? 'crm.quote.hidePricesBody' : 'crm.quote.showPricesBody'),
      confirmLabel: t('common.save'),
    })
    if (!reason?.trim()) return
    act.mutate({ id: offer.id, action: 'line-prices', body: { show: !f.showLinePrices, reason } }, { onSuccess: () => toast.success(t('crm.quote.pricesChanged')), onError: toastError })
  }
  const close = async (status: 'accepted' | 'rejected') => {
    const ok = await confirmModal({
      title: t(`crm.offer.confirm.${status}`, { number: offer.number }),
      description: t(`crm.offer.confirm.${status}Body`),
      confirmLabel: t(`crm.offer.action.${status}`),
      destructive: status === 'rejected',
    })
    if (ok) setStatus.mutate({ id: offer.id, status }, { onSuccess: () => toast.success(t(`crm.offer.statusChanged.${status}`)), onError: toastError })
  }

  return (
    <Modal open size="2xl" onOpenChange={(o) => !o && onClose()} icon={<FileSignature />} title={t('crm.quote.flowTitle', { number: offer.number })} description={t('crm.quote.flowHint')}>
      {!f ? (
        <Skeleton className="h-64" />
      ) : (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone={FLOW_TONE[f.status]}>{t(`crm.quote.status.${f.status}`)}</Badge>
            <Badge tone="neutral">{t('crm.quote.revision', { rev: f.revision })}</Badge>
            {f.isFinal ? <Badge tone="info">{t('crm.quote.final')}</Badge> : null}
            <Badge tone={f.tier === 'staff' ? 'neutral' : 'warning'}>{t(`crm.quote.tier.${f.tier}`, { pct: f.effectiveDiscount })}</Badge>
            {f.currentApproved && f.approvedBy ? (
              <span className="text-xs text-muted-foreground">{t('crm.quote.approvedBy', { name: staffName(f.approvedBy), at: fmtDateTime(f.approvedAt) })}</span>
            ) : null}
            <span className="ml-auto text-lg font-semibold tabular-nums">{fmtMoney(offer.total)}</span>
          </div>

          <div className="flex flex-wrap gap-2">
            {f.canEdit ? (
              <Button size="sm" variant="outline" onClick={() => setEditing(true)}>
                <Pencil />
                {t('crm.quote.revise')}
              </Button>
            ) : null}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" variant="outline">
                  <Download />
                  {t('crm.quote.document')}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem onSelect={() => void downloadQuote(offer.id, 'pdf').catch(toastError)}>PDF</DropdownMenuItem>
                <DropdownMenuItem onSelect={() => void downloadQuote(offer.id, 'docx').catch(toastError)}>Word (.docx)</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            {f.canTogglePrices ? (
              <Button size="sm" variant="outline" onClick={() => void togglePrices()}>
                {f.showLinePrices ? <EyeOff /> : <Eye />}
                {t(f.showLinePrices ? 'crm.quote.hidePrices' : 'crm.quote.showPrices')}
              </Button>
            ) : null}
            {f.canSend ? (
              <Button size="sm" onClick={() => setSending(true)}>
                <Send />
                {t('crm.quote.send')}
              </Button>
            ) : null}
            {['approved', 'sent', 'negotiation', 'final'].includes(f.status) ? (
              <>
                <Button size="sm" variant="outline" onClick={() => void close('rejected')}>
                  <XCircle />
                  {t('crm.offer.action.rejected')}
                </Button>
                <Button size="sm" variant="outline" className="text-success" onClick={() => void close('accepted')}>
                  <CheckCircle2 />
                  {t('crm.offer.action.accepted')}
                </Button>
              </>
            ) : null}
          </div>

          {f.canSubmit || f.canDecide || f.canNegotiate ? (
            <div className="space-y-2 rounded-lg border bg-muted/30 p-3">
              <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder={t(f.canDecide ? 'crm.quote.decisionNote' : f.canNegotiate ? 'crm.quote.negotiationNote' : 'crm.quote.submitNote')} />
              <div className="flex flex-wrap items-center justify-end gap-2">
                {f.canSubmit ? (
                  <>
                    <div className="mr-auto">
                      <CheckRow checked={final} onChange={(e) => setFinal(e.target.checked)}>
                        {t('crm.quote.markFinal')}
                      </CheckRow>
                    </div>
                    <Button size="sm" onClick={() => run('submit', { note, is_final: final }, 'crm.quote.submitted')} disabled={act.isPending}>
                      <Send />
                      {t('crm.quote.submit')}
                    </Button>
                  </>
                ) : null}
                {f.canDecide ? (
                  <>
                    <Button size="sm" variant="outline" disabled={needNote || act.isPending} onClick={() => run('decide', { action: 'request_changes', note }, 'crm.quote.changesRequested')}>
                      <CircleHelp />
                      {t('crm.quote.requestChanges')}
                    </Button>
                    <Button size="sm" variant="outline" className="text-destructive" disabled={needNote || act.isPending} onClick={() => run('decide', { action: 'reject', note }, 'crm.quote.rejected')}>
                      <XCircle />
                      {t('crm.quote.reject')}
                    </Button>
                    <Button size="sm" disabled={act.isPending} onClick={() => run('decide', { action: 'approve', note }, 'crm.quote.approved')}>
                      <CheckCircle2 />
                      {t(f.status === 'manager_review' ? 'crm.quote.approveManager' : 'crm.quote.approveOwner')}
                    </Button>
                  </>
                ) : null}
                {f.canNegotiate ? (
                  <Button size="sm" variant="outline" disabled={needNote || act.isPending} onClick={() => run('negotiate', { note }, 'crm.quote.negotiationSaved')}>
                    <Handshake />
                    {t('crm.quote.negotiate')}
                  </Button>
                ) : null}
              </div>
              {f.canDecide ? <p className="text-xs text-muted-foreground">{t('crm.quote.decideHint')}</p> : null}
            </div>
          ) : null}

          <Tabs value={tab} onValueChange={(v) => setTab(v as Tab)}>
            <TabsList size="sm">
              <TabsTrigger value="process">{t('crm.quote.tabs.process')}</TabsTrigger>
              <TabsTrigger value="revisions">{t('crm.quote.tabs.revisions', { count: f.revisions.length })}</TabsTrigger>
              <TabsTrigger value="sends">{t('crm.quote.tabs.sends', { count: f.sends.length })}</TabsTrigger>
              {f.visibility ? <TabsTrigger value="visibility">{t('crm.quote.tabs.visibility')}</TabsTrigger> : null}
            </TabsList>
          </Tabs>

          {tab === 'process' ? (
            <section className="space-y-2">
              <ol className="space-y-2">
                {f.comments.map((c, i) => (
                  <li key={i} className={cn('rounded-lg border px-3 py-2 text-sm', c.kind === 'negotiation' && 'border-warning/50 bg-warning/5', c.kind === 'decision' && 'bg-muted/40')}>
                    <p className="text-xs text-muted-foreground">
                      <span className="font-medium text-foreground">{c.userId ? staffName(c.userId) : t('crm.quote.system')}</span> · {t(`crm.quote.commentKind.${c.kind}`)} · Rev. {c.revision} ·{' '}
                      <span className="tabular-nums">{fmtDateTime(c.createdAt)}</span>
                    </p>
                    <p className="mt-0.5 whitespace-pre-wrap">{c.body}</p>
                  </li>
                ))}
              </ol>
              <CommentBox onSend={(body) => run('comments', { body }, 'crm.quote.commented')} pending={act.isPending} />
            </section>
          ) : null}

          {tab === 'revisions' ? (
            <ol className="space-y-2">
              {f.revisions.map((r, i) => {
                const prev = f.revisions[i + 1]
                const diff = revisionDiff(prev?.snapshot, r.snapshot, names)
                return (
                  <li key={r.revision} className="rounded-lg border px-3 py-2 text-sm">
                    <p className="flex flex-wrap items-center gap-2">
                      <Badge tone={r.revision === f.revision ? 'info' : 'neutral'}>Rev. {r.revision}</Badge>
                      {f.currentApproved && r.revision === f.revision ? <Badge tone="success">{t('crm.quote.approvedRev')}</Badge> : null}
                      <span className="font-medium">{r.reason}</span>
                      <span className="ml-auto text-xs text-muted-foreground">
                        {staffName(r.createdBy)} · {fmtDateTime(r.createdAt)} · {fmtMoney(String(r.snapshot.total ?? '0'))}
                      </span>
                    </p>
                    {diff.length ? (
                      <ul className="mt-1.5 space-y-0.5 text-xs">
                        {diff.map((d, j) => (
                          <li key={j} className="flex flex-wrap items-center gap-1.5">
                            <GitCompare className="size-3 text-muted-foreground" aria-hidden />
                            <Badge tone={d.kind === 'added' ? 'success' : d.kind === 'removed' ? 'danger' : 'warning'}>{t(`crm.quote.diff.${d.kind}`)}</Badge>
                            <span className="font-medium">{t(`crm.quote.diffField.${d.label}`, { defaultValue: d.label })}</span>
                            {d.before ? <span className="text-muted-foreground line-through">{d.before}</span> : null}
                            {d.after ? <span>{d.after}</span> : null}
                          </li>
                        ))}
                      </ul>
                    ) : null}
                  </li>
                )
              })}
            </ol>
          ) : null}

          {tab === 'sends' ? (
            f.sends.length ? (
              <ul className="space-y-2">
                {f.sends.map((x, i) => (
                  <li key={i} className="rounded-lg border px-3 py-2 text-xs">
                    <p className="font-medium">
                      Rev. {x.revision} · {staffName(x.sentBy)} · {fmtDateTime(x.createdAt)} · {x.format.toUpperCase()}
                    </p>
                    <p className="text-muted-foreground">{t('crm.quote.sentTo', { to: x.recipients.join(', '), cc: x.cc.join(', ') })}</p>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground">{t('crm.quote.noSends')}</p>
            )
          ) : null}

          {tab === 'visibility' && f.visibility ? (
            f.visibility.length ? (
              <ul className="space-y-2">
                {f.visibility.map((v, i) => (
                  <li key={i} className="rounded-lg border px-3 py-2 text-xs">
                    <p className="font-medium">
                      {t(v.show ? 'crm.quote.pricesShown' : 'crm.quote.pricesHidden')} · {staffName(v.userId)} · {fmtDateTime(v.createdAt)}
                    </p>
                    <p className="text-muted-foreground">{v.reason}</p>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground">{t('crm.quote.noVisibility')}</p>
            )
          ) : null}
        </div>
      )}
      {editing ? <OfferForm customerId={offer.customerId} offer={offer} onDone={() => setEditing(false)} /> : null}
      {sending ? <SendQuoteModal quoteId={offer.id} onClose={() => setSending(false)} /> : null}
    </Modal>
  )
}

function CommentBox({ onSend, pending }: { onSend: (body: string) => void; pending: boolean }) {
  const { t } = useTranslation()
  const [body, setBody] = useState('')
  return (
    <div className="flex gap-2">
      <Textarea rows={2} value={body} maxLength={4000} onChange={(e) => setBody(e.target.value)} placeholder={t('crm.quote.commentPlaceholder')} />
      <Button
        size="sm"
        variant="outline"
        className="self-end"
        disabled={!body.trim() || pending}
        onClick={() => {
          onSend(body)
          setBody('')
        }}
      >
        <MessageSquare />
        {t('crm.quote.comment')}
      </Button>
    </div>
  )
}
